import { computed, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { useEventListener, useResizeObserver } from '@vueuse/core';

/** A template ref the caller binds in its own template, e.g. via `useTemplateRef`. */
type ElementRef = Readonly<Ref<HTMLElement | null | undefined>>;

interface PagedScrollOptions {
  /** Leading children of the track that are not pages, e.g. a sticky table label cell. */
  leading?: MaybeRefOrGetter<number>;
  /** The scrolling box, bound by the caller that renders it. */
  scrollArea?: ElementRef;
  /** Holds the paged children; omit when the scroll area holds them itself. */
  track?: ElementRef;
  /** The scrolling element, for a caller that does not render it and so cannot bind `scrollArea`. */
  target?: MaybeRefOrGetter<HTMLElement | null | undefined>;
}

/** Tracks a horizontally scrolling element as discrete pages and as fractions of the content. */
export function usePagedScroll({ leading = 0, scrollArea, track, target }: PagedScrollOptions = {}) {
  const element = computed(() => toValue(target) ?? scrollArea?.value);

  const pageCount = ref(1);
  // Cached so tracking the active page on every scroll event never reads layout.
  const step = ref(1);
  const scrolled = ref(0);
  const visibleWidth = ref(0);
  const contentWidth = ref(0);

  function items(): HTMLElement[] {
    const children = Array.from((track?.value ?? element.value)?.children ?? []) as HTMLElement[];

    return children.slice(toValue(leading));
  }

  // Distance between two consecutive children, so gaps and paddings count. `Math.abs` for RTL.
  function measureStep(box: HTMLElement): number {
    const children = items();
    const distance = children.length > 1 ? Math.abs(children[1]!.offsetLeft - children[0]!.offsetLeft) : 0;

    // A collapsed container measures zero on both; never divide by it.
    return distance || box.clientWidth || 1;
  }

  function measurePages() {
    const box = element.value;

    // A collapsed container says nothing about the page count; reset until the observer re-runs.
    if (!box?.clientWidth) {
      pageCount.value = 1;
      step.value = 1;
      scrolled.value = 0;
      visibleWidth.value = 0;
      contentWidth.value = 0;

      return;
    }

    visibleWidth.value = box.clientWidth;
    contentWidth.value = box.scrollWidth;
    // Read here too: a responsive caller may swap in an element resting at its own offset.
    scrolled.value = Math.abs(box.scrollLeft);

    const maxScroll = contentWidth.value - visibleWidth.value;

    step.value = measureStep(box);
    const wholeSteps = Math.floor(maxScroll / step.value);
    // A leftover shorter than a child is still its own page.
    const trailingPage = maxScroll - wholeSteps * step.value > 1 ? 1 : 0;

    pageCount.value = maxScroll > 1 ? wholeSteps + trailingPage + 1 : 1;
  }

  const activePage = computed(() => {
    const maxScroll = contentWidth.value - visibleWidth.value;

    // The end is always the last page, which a division misses when that page is a partial one.
    return scrolled.value >= maxScroll - 1
      ? pageCount.value - 1
      : Math.min(pageCount.value - 1, Math.round(scrolled.value / step.value));
  });

  /** How much of the content is in view. One or more means it all is, so nothing scrolls. */
  const visibleFraction = computed(() => (contentWidth.value ? visibleWidth.value / contentWidth.value : 1));
  /** How far along the content the view sits, as a share of the whole. */
  const scrolledFraction = computed(() => (contentWidth.value ? scrolled.value / contentWidth.value : 0));

  /** Brings the page's leading child flush to the start; `scrollIntoView` handles padding and RTL. */
  function scrollToPage(page: number) {
    items()[page]?.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
  }

  // Owned here: a `target` is rendered elsewhere, so no caller can bind `@scroll` on it.
  useEventListener(element, 'scroll', () => {
    scrolled.value = Math.abs(element.value?.scrollLeft ?? 0);
  }, { passive: true });

  // Re-measures on resize, and re-targets when a responsive caller swaps its scroll area.
  useResizeObserver(element, measurePages);

  return {
    activePage,
    pageCount,
    visibleFraction,
    scrolledFraction,
    measurePages,
    scrollToPage,
  };
}
